import { mvpApiGetPlanogramOptions } from "@labeling-tools/api/atlas/@tanstack/react-query.gen";
import { useQuery } from "@tanstack/react-query";
import { ActivePlanoProductPopover } from "./active-label-popover/active-plano-product-popover.comp";
import { PlanoLabelBoxes } from "./plano-label-boxes/plano-label-boxes.comp";
import { useActivePlanoProductInfoStore } from "./use-active-plano-product-info-store";
import { useRef, useEffect } from "react";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import {
  getLabelDimension,
  getPlanoDimension,
} from "../utils/plano-related.utils";

interface Props {
  planogram_id?: string;
  /** If provided, will auto-highlight and zoom to this product when the planogram loads */
  initialProductId?: string | null;
  hidePopover?: boolean;
}

export const PlanogramViewer = (props: Props) => {
  const { planogram_id, initialProductId, hidePopover = false } = props;
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const { data: planogramResp } = useQuery({
    ...mvpApiGetPlanogramOptions({
      path: { planogram_id: planogram_id || "" },
    }),
    enabled: !!planogram_id,
  });

  const activePlanogramProductId = useActivePlanoProductInfoStore(
    (s) => s.productBoxInfo.planogram_product_id,
  );

  const setActiveLabelBoxInfo = useActivePlanoProductInfoStore(
    (s) => s.setActiveLabelBoxInfo,
  );

  // Auto-highlight and zoom to initial product when planogram loads
  useEffect(() => {
    if (!planogramResp || !initialProductId) return;

    const planogramProducts = planogramResp.products ?? [];
    const targetProduct = planogramProducts.find(
      (p) => p.id === initialProductId || p.product_id === initialProductId,
    );

    if (!targetProduct) return;

    // Calculate dimensions
    const planoDimension = getPlanoDimension(planogramProducts);
    const labelDimension = getLabelDimension(targetProduct);

    // Calculate position as percentage (same as OneLabelBox)
    const leftPercent =
      (labelDimension.x0 - planoDimension.x0) / planoDimension.w;
    const topPercent =
      (labelDimension.y0 - planoDimension.y0) / planoDimension.h;
    const widthPercent = labelDimension.w / planoDimension.w;
    const heightPercent = labelDimension.h / planoDimension.h;

    // Calculate center of the product for zoom target
    const centerX = leftPercent + widthPercent / 2;
    const centerY = topPercent + heightPercent / 2;

    // Use setTimeout to ensure TransformWrapper has mounted and ref is available
    const timeoutId = setTimeout(() => {
      const container = document.getElementById("planogram-view-container");
      if (!container || !transformRef.current) return;

      const containerRect = container.getBoundingClientRect();

      // Calculate zoom scale (fit product with some padding)
      const scaleX = 1 / (widthPercent * 2); // 2x padding
      const scaleY = 1 / (heightPercent * 2);
      const scale = Math.min(scaleX, scaleY, 5); // Cap at 5x zoom

      // Calculate the position to center the product
      const positionX =
        containerRect.width / 2 - centerX * containerRect.width * scale;
      const positionY =
        containerRect.height / 2 - centerY * containerRect.height * scale;

      // Apply zoom and pan
      transformRef.current.setTransform(
        positionX,
        positionY,
        scale,
        300,
        "easeOut",
      );

      // Set active product info for highlighting/popover
      setTimeout(() => {
        setActiveLabelBoxInfo({
          left: containerRect.width * leftPercent,
          top: containerRect.height * topPercent,
          planogram_product_id: targetProduct.id,
          product_id: targetProduct.product_id,
        });
      }, 350);
    }, 100); // Small delay to ensure TransformWrapper mounts

    return () => clearTimeout(timeoutId);
  }, [planogramResp, initialProductId, setActiveLabelBoxInfo]);

  if (!planogramResp) return null;
  return (
    <>
      <div
        id="planogram-view-container"
        className="relative flex h-[400px] items-center justify-center border border-gray-400 bg-gray-200"
      >
        {!!planogramResp && (
          <>
            <TransformWrapper
              ref={transformRef}
              initialScale={1}
              minScale={1}
              maxScale={Infinity}
              pinch={{ step: 5 }}
              doubleClick={{ disabled: true }}
              panning={{ velocityDisabled: true }}
            >
              <TransformComponent
                wrapperStyle={{ width: "100%", height: "100%" }}
                contentStyle={{ width: "100%", height: "100%" }}
              >
                <PlanoLabelBoxes planogram={planogramResp} />
              </TransformComponent>
            </TransformWrapper>

            {!!activePlanogramProductId?.length && !hidePopover && (
              <ActivePlanoProductPopover
                planogram_id={planogramResp.id || ""}
                activePlanogramProductId={activePlanogramProductId}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
